import React, { useCallback } from 'react';
import { TRANSITION_STROKE_WIDTH } from './ellipsis.tsx';
import { EDGE_TYPE } from './directionKeys.tsx';
import type { ValidationFlag, PortInfo } from './toPortInfo.tsx';
import type { ItemReference } from './ListEdgeDetails.tsx';
import { DiagramArrow } from './DiagramArrow.tsx';
import { useStrokeColor } from './useStrokeColor.tsx';

export interface Props {
	id: string;
	flag?: ValidationFlag;
	isHovered?: boolean;
	isHighlighted?: boolean;
	isImmutable?: boolean;
	isSelected?: boolean;
	isTargeted?: boolean;
	start: PortInfo | undefined;
	end: PortInfo | undefined;
	path: string | undefined;
	onSelect?: (item: ItemReference | undefined) => void;
	onHover?: (item: ItemReference | undefined) => void;
}

const DirectTransition = ({
	id,
	flag,
	isHighlighted = false,
	isHovered = false,
	isImmutable = false,
	isSelected = false,
	end,
	start,
	path,
	onSelect,
	onHover,
}: Props) => {
	const stroke = useStrokeColor({
		flag,
		isHighlighted,
		isHovered,
		isSelected,
	});

	const onGroupClick = useCallback(() => {
		if (!isImmutable) {
			onSelect?.({ type: EDGE_TYPE, id });
		}
	}, [isImmutable, onSelect, id]);

	const onMouseEnter = useCallback(() => {
		onHover?.({
			id,
			type: EDGE_TYPE,
			connection: {
				sourcePort: start,
				targetPort: end,
			},
		});
	}, [end, id, onHover, start]);

	const onMouseLeave = useCallback(() => {
		onHover?.(undefined);
	}, [onHover]);

	if (!start || !end) {
		return null;
	}

	if (start.portIndex === undefined || end.portIndex === undefined) {
		return null;
	}

	return (
		<g onClick={onGroupClick} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
			{/* Click and hover area */}
			<path fill="none" stroke="transparent" d={path} strokeLinejoin="round" strokeWidth="15" />
			{/* Routed path of transition */}
			<path
				fill="none"
				stroke={stroke}
				d={path}
				strokeLinejoin="round"
				strokeWidth={TRANSITION_STROKE_WIDTH}
			/>
			<DiagramArrow portIndex={end.portIndex} portPosition={end.position} stroke={stroke} />
		</g>
	);
};

export default DirectTransition;
